import axios from 'axios';
import axiosInstance from 'config/axiosConfig';
import { Region, ResponseItem } from 'types/Region';
import { containsWithoutSpecialChars } from 'util/containsWithoutSpecialChars';
import { create } from 'zustand';

interface Item {
    title: string;
    lock: boolean;
}

interface RegionState {
    fetchRegions: () => Promise<Region[]>;
    regionList: Region[];
    totalCities: Item[];
    allStates: Item[];
    getStates: (lock: boolean, region: Region) => ResponseItem[];
    getNonStates: (region: Region) => ResponseItem[];
    getFilteredCities: (searchTerm: string) => Item[];
    getSelectedStateCities: (selectedState: string) => (string | undefined)[];
    getSelectedRegionCities: (selectedState: string, region: Region) => string[];
    getCities: (selectedState: string, region: Region) => (string | undefined)[];
    getNonCities: (selectedState: string, region: Region) => (string | undefined)[];
    getFilteredStates: (searchTerm: string) => Item[];
    totalNeighborhoods: Item[];
    getFilteredNeighborhoods: (searchTerm: string) => Item[];
    getSelectedRegionNeighborhoods: (region: Region) => string[];
    getSelectedStatesNeighborhoods: (selectedStates: string[]) => string[];
    getSelectedCitiesNeighborhoods: (selectedCities: string[]) => string[];
    getNeighborhoods: (region: Region, selectedState: string[], selectedCities: string[]) => string[];
    getSelectedRegionNonNeighborhoods: (region: Region) => string[];
    getSelectedStatesNonNeighborhoods: (selectedStates: string[]) => string[];
    getSelectedCitiesNonNeighborhoods: (selectedCities: string[]) => string[];
    getNonNeighborhoods: (region: Region, selectedStates: string[], selectedCities: string[]) => string[];
}

const useRegionStore = create<RegionState>((set, get) => ({
    regionList: [],
    totalCities: [],
    allStates: [],
    totalNeighborhoods: [],
    fetchRegions: async () => {
        try {
            const { data } = await axiosInstance.get(`/properties/filters`);

            let allCities: Item[] = [];
            let allStates: Item[] = [];
            let allNeighborhoods: Item[] = [];
            data.data.countriesAndCities.map((region: Region) => {
                allCities = allCities.concat(
                    region.citiesAndStates
                        .map((cityAndState) => {
                            return cityAndState.Cities.map((city) => ({ title: city.City, lock: false }));
                        })
                        .flat(),
                );
                allCities = allCities.concat(
                    region.nonCitiesAndStates
                        .filter((cityAndState) => !region.citiesAndStates.some((state) => state.Cities.some((city) => cityAndState.Cities.includes(city))))
                        .map((cityAndState) => {
                            return cityAndState.Cities.map((city) => ({ title: city.City, lock: true }));
                        })
                        .flat(),
                );

                allStates = allStates.concat(region.citiesAndStates.map((state) => ({ title: state.State, lock: false })));
                allStates = allStates.concat(
                    region.nonCitiesAndStates
                        .filter((state) => !region.citiesAndStates.some((cityState) => cityState.State === state.State))
                        .map((state) => ({ title: state.State, lock: true })),
                );
                allNeighborhoods = allNeighborhoods.concat(
                    region.citiesAndStates
                        .map((cityState) => cityState.Cities.map((city) => city.Neighborhoods.map((neighborhood) => ({ title: neighborhood, lock: false }))))
                        .flat(2),
                );
                allNeighborhoods = allNeighborhoods.concat(
                    region.nonCitiesAndStates
                        .map((cityState) => cityState.Cities.map((city) => city.Neighborhoods.map((neighborhood) => ({ title: neighborhood, lock: true }))))
                        .flat(2),
                );
            });

            set({
                regionList: data.data.countriesAndCities,
                totalCities: allCities,
                allStates: allStates,
                totalNeighborhoods: allNeighborhoods,
            });

            return data.data.countriesAndCities;
        } catch (error) {
            if (axios.isAxiosError(error)) throw new Error(error.response?.data.message);
        }
    },
    getStates: (lock, region) => {
        return lock
            ? region.citiesAndStates
            : [
                  ...region.citiesAndStates,
                  ...region.nonCitiesAndStates.filter((state) => !region.citiesAndStates.some((cityState) => cityState.State === state.State)),
              ];
    },

    getNonStates: (region) => {
        return region.nonCitiesAndStates.filter((state) => !region.citiesAndStates.some((cityState) => cityState.State === state.State));
    },
    getFilteredCities: (searchTerm) => {
        const { totalCities } = get();
        return totalCities
            .sort((a, b) => {
                if (a.lock && !b.lock) return 1;
                if (!a.lock && b.lock) return -1;
                return a.title.localeCompare(b.title);
            })
            .filter((city) => containsWithoutSpecialChars(city.title, searchTerm))
            .slice(0, 100);
    },
    getSelectedStateCities: (selectedState) => {
        const { regionList } = get();
        return regionList.map((region) => region.citiesAndStates.find((city) => city.State === selectedState)?.Cities.map((city) => city.City)).flat();
    },

    getSelectedRegionCities: (selectedState, region) => {
        return region.citiesAndStates
            .filter((city) => !selectedState || city.State === selectedState)
            .map((city) => city.Cities.map((city) => city.City))
            .flat();
    },
    getCities: (selectedState, region) => {
        const { getSelectedStateCities, getSelectedRegionCities } = get();
        return selectedState ? getSelectedStateCities(selectedState) : getSelectedRegionCities(selectedState, region);
    },
    getNonCities: (selectedState, region) => {
        const { regionList, getSelectedStateCities, getSelectedRegionCities } = get();
        return selectedState
            ? regionList
                  .map((region) => region.nonCitiesAndStates.find((city) => city.State === selectedState)?.Cities.map((city) => city.City))
                  .flat()
                  .filter((city) => !getSelectedStateCities(selectedState).includes(city))
            : region.nonCitiesAndStates
                  .filter((city) => !selectedState || city.State === selectedState)
                  .map((city) => city.Cities.map((city) => city.City))
                  .flat()
                  .filter((city) => !getSelectedRegionCities(selectedState, region).includes(city));
    },
    getFilteredStates: (searchTerm) => {
        const { allStates } = get();
        return allStates
            .filter((state) => containsWithoutSpecialChars(state.title, searchTerm))
            .sort((a, b) => (a.lock ? 1 : -1) - (b.lock ? 1 : -1))
            .slice(0, 100);
    },
    getFilteredNeighborhoods: (searchTerm) => {
        const { totalNeighborhoods } = get();
        return totalNeighborhoods
            .sort((a, b) => {
                if (a.lock && !b.lock) return 1;
                if (!a.lock && b.lock) return -1;
                return a.title.localeCompare(b.title);
            })
            .filter((neighborhood) => containsWithoutSpecialChars(neighborhood.title, searchTerm))
            .slice(0, 100);
    },
    getSelectedRegionNeighborhoods: (region) => {
        return region.citiesAndStates.map((cityState) => cityState.Cities.map((city) => city.Neighborhoods.map((neighborhood) => neighborhood))).flat(2);
    },
    getSelectedStatesNeighborhoods: (selectedStates) => {
        const { regionList } = get();
        return regionList
            .map((region) =>
                region.citiesAndStates
                    .filter((cityState) => selectedStates.includes(cityState.State))
                    .map((cityState) => cityState.Cities.map((city) => city.Neighborhoods.map((neighborhood) => neighborhood)))
                    .flat(2),
            )
            .flat(2) as string[];
    },
    getSelectedCitiesNeighborhoods: (selectedCities) => {
        const { regionList } = get();
        return regionList
            .map((region) =>
                region.citiesAndStates
                    .map((cityState) =>
                        cityState.Cities.filter((city) => selectedCities.includes(city.City))
                            .map((city) => city.Neighborhoods)
                            .flat(),
                    )
                    .flat(),
            )
            .flat() as string[];
    },
    getNeighborhoods: (region, selectedStates, selectedCities) => {
        const { getSelectedStatesNeighborhoods, getSelectedCitiesNeighborhoods, getSelectedRegionNeighborhoods, totalNeighborhoods } = get();
        if (region.name) {
            return getSelectedRegionNeighborhoods(region);
        } else {
            return selectedStates.length > 0
                ? getSelectedStatesNeighborhoods(selectedStates)
                : selectedCities.length > 0
                  ? getSelectedCitiesNeighborhoods(selectedCities)
                  : Array.from(
                        new Set(
                            totalNeighborhoods
                                .filter((neighborhood) => !neighborhood.lock && neighborhood.title.trim() !== '')
                                .map((neighborhood) => neighborhood.title),
                        ),
                    );
        }
    },
    getSelectedRegionNonNeighborhoods: (region) => {
        const { getSelectedRegionNeighborhoods } = get();
        return region.nonCitiesAndStates
            .map((cityState) => cityState.Cities.map((city) => city.Neighborhoods.map((neighborhood) => neighborhood)))
            .flat(2)
            .filter((neighborhood) => !getSelectedRegionNeighborhoods(region).includes(neighborhood));
    },
    getSelectedStatesNonNeighborhoods: (selectedStates) => {
        const { regionList, getSelectedStatesNeighborhoods } = get();
        return regionList
            .map((region) =>
                region.nonCitiesAndStates
                    .filter((cityState) => selectedStates.includes(cityState.State))
                    .map((cityState) => cityState.Cities.map((city) => city.Neighborhoods.map((neighborhood) => neighborhood)))
                    .flat(2),
            )
            .flat(2)
            .filter((neighborhood) => !getSelectedStatesNeighborhoods(selectedStates).includes(neighborhood));
    },
    getSelectedCitiesNonNeighborhoods: (selectedCities) => {
        const { regionList, getSelectedCitiesNeighborhoods } = get();
        return regionList
            .map((region) =>
                region.nonCitiesAndStates
                    .map((cityState) =>
                        cityState.Cities.filter((city) => selectedCities.includes(city.City))
                            .map((city) => city.Neighborhoods)
                            .flat(),
                    )
                    .flat(),
            )
            .flat()
            .filter((neighborhood) => !getSelectedCitiesNeighborhoods(selectedCities).includes(neighborhood));
    },
    getNonNeighborhoods: (region, selectedStates, selectedCities) => {
        const { getSelectedRegionNonNeighborhoods, getSelectedStatesNonNeighborhoods, getSelectedCitiesNonNeighborhoods, totalNeighborhoods } = get();
        if (region.name) {
            return getSelectedRegionNonNeighborhoods(region);
        } else {
            return selectedStates.length > 0
                ? getSelectedStatesNonNeighborhoods(selectedStates)
                : selectedCities.length > 0
                  ? getSelectedCitiesNonNeighborhoods(selectedCities)
                  : Array.from(
                        new Set(
                            totalNeighborhoods
                                .filter((neighborhood) => neighborhood.lock && neighborhood.title.trim() !== '')
                                .map((neighborhood) => neighborhood.title),
                        ),
                    );
        }
    },
}));

export default useRegionStore;
